import React, { useEffect, useMemo, useState } from 'react';
import { Button, Space, Spin, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import {
  downloadFromUrl,
  PDF_VIEW_HASH,
  VIEWER_BACKDROP,
  viewerPreviewKind,
} from './previewKind';

const { Text } = Typography;

/**
 * How long to leave the "Preparing Preview..." veil up before giving up on it.
 *
 * ⚠️ Not cosmetic. The veil is lifted by the frame's `onLoad`, and neither an
 * `<object>` nor its fallback `<iframe>` reliably fires one - it depends on the
 * browser and on whether a PDF plugin is enabled at all. Without this the
 * spinner can cover a document that has already rendered, with nothing the user
 * can do but reselect the file.
 */
const RENDER_VEIL_TIMEOUT_MS = 8000;

interface DocumentFrameProps {
  /** A signed read URL, or null while there is nothing to show. */
  url: string | null;
  fileName?: string;
  contentType?: string | null;
}

/**
 * The document itself, drawn the way its type allows.
 *
 * Office formats and anything unrecognised get the download instead of a broken
 * frame - there is no converter in front of this, so offering the file is the
 * only honest thing left to offer.
 */
const DocumentFrame: React.FC<DocumentFrameProps> = ({
  url,
  fileName,
  contentType,
}) => {
  const [isRendering, setIsRendering] = useState(false);

  const kind = useMemo(
    () => viewerPreviewKind(fileName, contentType),
    [fileName, contentType],
  );

  const pdfUrl = useMemo(
    () => (url && kind === 'pdf' ? `${url}${PDF_VIEW_HASH}` : null),
    [url, kind],
  );

  useEffect(() => {
    setIsRendering(!!url && kind === 'pdf');
  }, [url, kind]);

  useEffect(() => {
    if (!isRendering) return;
    const timer = setTimeout(
      () => setIsRendering(false),
      RENDER_VEIL_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [isRendering]);

  if (!url) return null;

  const content = () => {
    switch (kind) {
      case 'pdf':
        return (
          <object
            data={pdfUrl!}
            type="application/pdf"
            width="100%"
            height="100%"
            onLoad={() => setIsRendering(false)}
            style={{
              border: 'none',
              display: 'block',
              visibility: isRendering ? 'hidden' : 'visible',
            }}
          >
            <iframe
              title="pdf-viewer"
              src={pdfUrl!}
              style={{ width: '100%', height: '100%', border: 'none' }}
              onLoad={() => setIsRendering(false)}
            />
          </object>
        );

      case 'image':
        return (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              padding: 16,
              overflow: 'auto',
            }}
          >
            <img
              src={url}
              alt={fileName || 'Document'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            />
          </div>
        );

      case 'text':
        return (
          <iframe
            title="text-viewer"
            src={url}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              backgroundColor: '#fff',
            }}
          />
        );

      case 'office':
      case 'unsupported':
      default:
        return (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
            }}
          >
            <Space direction="vertical" align="center" size="middle">
              <Text style={{ color: '#fff', fontSize: 14 }}>
                {kind === 'office'
                  ? 'Preview not available for Office documents.'
                  : 'This file type is not supported for preview.'}
              </Text>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={() => downloadFromUrl(url, fileName)}
              >
                Download Instead
              </Button>
            </Space>
          </div>
        );
    }
  };

  return (
    <>
      {isRendering && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            zIndex: 10,
            backgroundColor: VIEWER_BACKDROP,
          }}
        >
          <Space direction="vertical" align="center">
            <Spin size="large" />
            <Text style={{ color: '#fff' }}>Preparing Preview...</Text>
          </Space>
        </div>
      )}
      {content()}
    </>
  );
};

export default DocumentFrame;
